// ── The IDV formula ─────────────────────────────────────────────────────────
//
//     idv          = round(price x (1 - depreciationPercent%))
//     idvRangeFrom = round(idv x minPercent%)
//     idvRangeTo   = round(idv x maxPercent%)
//
// e.g. price 1,23,000 with 5% depreciation -> IDV 1,16,850, and a 95%-105%
// band gives 1,11,008 to 1,22,693.
//
// minPercent/maxPercent are ONE saved record (backend collection "formula",
// document IDV_RANGE) that an admin edits from the IDV Calculator, so changing
// the band no longer needs a code change. depreciationPercent lives on each
// Make & Model record — it depends on the vehicle and its age — and the
// formula's value is only the default for one that has none.
//
// The defaults below are what every helper uses until a saved formula has been
// loaded, so a slow or failed fetch still derives a sane range.

export const DEFAULT_IDV_FORMULA = {
  depreciationPercent: 5,
  minPercent: 95,
  maxPercent: 105,
};

let activeFormula = { ...DEFAULT_IDV_FORMULA };

// A DELIBERATE 0 and an empty field are different things: 0% depreciation means
// "value it at the full price", while blank means "no value set, use the
// formula's". Number("") and Number(null) are both 0, so testing only for a
// finite number made a blank field silently behave as 0% — the opposite of
// what the form promises. Absence is checked before the conversion.
const isBlank = (value) =>
  value === "" || value === null || value === undefined;

const asNumber = (value, fallback) => {
  if (isBlank(value)) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/** Fill in anything missing or unparseable from the defaults. */
const normalizeFormula = (formula) => ({
  depreciationPercent: asNumber(
    formula?.depreciationPercent,
    DEFAULT_IDV_FORMULA.depreciationPercent
  ),
  minPercent: asNumber(formula?.minPercent, DEFAULT_IDV_FORMULA.minPercent),
  maxPercent: asNumber(formula?.maxPercent, DEFAULT_IDV_FORMULA.maxPercent),
});

/**
 * Publish the saved formula to every caller that does not pass one explicitly.
 *
 * Called once after the formula is fetched. Module-level rather than context so
 * the plain helpers below — used from non-React code paths too — keep working
 * without every call site having to thread the formula through.
 */
export const setActiveIdvFormula = (formula) => {
  activeFormula = normalizeFormula(formula);
  return { ...activeFormula };
};

/** The formula currently in force. */
export const getActiveIdvFormula = () => ({ ...activeFormula });

/** A price with its non-numeric decoration stripped. NaN-safe. */
const toPrice = (value) => {
  const n = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * IDV for a selling price: the price less depreciation.
 *
 * @param {number|string} sellingPrice
 * @param {number|string} [depreciationPercent] - the vehicle's own value;
 *        falls back to the saved formula's default when not given
 * @returns {string} the IDV, or "" when the price is unusable
 */
export const calculateIdv = (sellingPrice, depreciationPercent) => {
  const price = toPrice(sellingPrice);
  if (!price) return "";
  const dep = asNumber(depreciationPercent, activeFormula.depreciationPercent);
  // A depreciation of 100% or more would value the vehicle at nothing, so the
  // result is floored rather than allowed to go negative.
  return String(Math.max(0, Math.round(price * (1 - dep / 100))));
};

/** Human-readable, e.g. "IDV × 95%". */
export const describeIdvSide = (percent) =>
  `IDV × ${asNumber(percent, 0)}%`;

/** Human-readable depreciation step, e.g. "Price − 5%". */
export const describeDepreciation = (percent) =>
  `Price − ${asNumber(percent, activeFormula.depreciationPercent)}%`;

/**
 * The IDV range in force for a Make & Model record.
 *
 * The STORED idvRangeFrom/idvRangeTo win. On the Make & Model master the
 * selling price is a calculator: typing it fills these two fields from the
 * saved formula, and they stay editable afterwards. So the stored pair already reflects the
 * price, unless someone deliberately adjusted it — and a deliberate adjustment
 * must not be silently ignored here.
 *
 * The price is only used to DERIVE a range when the record has none stored,
 * which covers records saved before the calculator existed.
 *
 * Every place that reads a range goes through here. Seven call sites across
 * two files previously read the stored fields directly while others derived
 * from the price, so a record whose price and range disagreed showed one range
 * in the hint, validated against a second, and auto-filled from a third — the
 * form rejecting the very IDV it had just filled in.
 *
 * @param {object} record - a Make & Model master row
 * @returns {{from: string, to: string}} blank strings when neither source is set
 */
export const resolveIdvRange = (record) => {
  const asText = (v) => (v === undefined || v === null ? "" : String(v).trim());

  const from = asText(record?.idvRangeFrom);
  const to = asText(record?.idvRangeTo);
  if (from || to) return { from, to };

  // No stored range — fall back to deriving one from the selling price, using
  // THIS vehicle's depreciation rather than the formula's default.
  const derived = deriveIdvRange(
    record?.manufacturerSellingPrice,
    undefined,
    record?.depreciationPercent
  );
  return { from: derived.from, to: derived.to };
};

/**
 * IDV range for an ex-showroom / manufacturer's selling price.
 *
 * Depreciation is applied first, then the band either side of the result:
 *   idv = price - depreciation,  from = idv x minPercent%,  to = idv x maxPercent%
 *
 * @param {number|string} sellingPrice
 * @param {object} [formula] - overrides the saved formula, for a live preview
 *                             of numbers the user is still typing
 * @param {number|string} [depreciationPercent] - the vehicle's own value;
 *        falls back to the formula's default when not given
 * @returns {{from: string, to: string, idv: string}} blanks when the price is unusable
 */
export const deriveIdvRange = (sellingPrice, formula, depreciationPercent) => {
  const price = toPrice(sellingPrice);
  if (!price) return { from: "", to: "", idv: "" };

  const f = formula ? normalizeFormula(formula) : activeFormula;
  const dep = asNumber(depreciationPercent, f.depreciationPercent);

  const idv = Math.max(0, Math.round(price * (1 - dep / 100)));
  // The band is a percentage OF THE IDV, not of the original price — applying
  // it to the price would widen every range by the depreciation as well.
  const side = (percent) => String(Math.max(0, Math.round((idv * percent) / 100)));

  return {
    idv: String(idv),
    from: side(f.minPercent),
    to: side(f.maxPercent),
  };
};

/**
 * Default IDV for a Make & Model record: the middle of its IDV range.
 *
 * The form used to pre-fill the range's LOWEST value, which quoted every
 * vehicle at its floor. The midpoint is the fairer default — 100 to 200 gives
 * 150 — and the user can still type any value inside the range.
 *
 * Returns "" when the record has no usable range, so the caller can leave the
 * field for manual entry rather than writing a bogus 0.
 *
 * @param {number|string} from - idvRangeFrom
 * @param {number|string} to   - idvRangeTo
 * @returns {string} the midpoint as a string, or "" when not derivable
 */
export const midpointIdv = (from, to) => {
  const low = Number(from);
  const high = Number(to);

  const hasLow = Number.isFinite(low) && from !== "" && from !== null;
  const hasHigh = Number.isFinite(high) && to !== "" && to !== null;

  // Only one end configured — that value is the only sensible default.
  if (hasLow && !hasHigh) return String(Math.round(low));
  if (!hasLow && hasHigh) return String(Math.round(high));
  if (!hasLow && !hasHigh) return "";

  // Tolerate a record saved with the ends the wrong way round.
  const min = Math.min(low, high);
  const max = Math.max(low, high);

  // Math.round can land a half-value one rupee outside a 1-wide range, so keep
  // the result inside the range the validator checks against.
  const mid = Math.round((min + max) / 2);
  return String(Math.min(Math.max(mid, min), max));
};
